import { ParsedSlashCommand } from 'conversations/components/SlashCommandPopup/SlashCommandParser'
import produce from 'immer'
import { reject } from 'ramda'
import { combineReducers } from 'redux'
import {
  addRoutedNotRespondedConversation,
  clearSlashCommand,
  createTagAsync,
  decrementBadgeCount,
  decrementCount,
  fetchAllConversationsAsync,
  fetchConversationAsync,
  fetchConversationLastSyncedAsync,
  fetchTagsAsync,
  hideConversationTranslationSettings,
  incrementBadgeCount,
  incrementCount,
  receiveBadgeCounts,
  receiveConversationUpdateFromWebsocket,
  receiveCounts,
  receivedConversationSync,
  receiveManuallyMarkedAsRead,
  receiveMostRecentlyReadMessageForConversation,
  receiveSlashCommand,
  receiveSubscriptionEvent,
  removeFileUpload,
  removePendingAttachments,
  removeRoutedNotRespondedConversation,
  showConversationTranslationSettings,
  updatePendingReadReceipt,
  uploadFileAsync,
} from 'shared/store/actions/conversation'
import {
  FETCH_ALL_CONVERSATIONS_REQUEST,
  FETCH_ALL_CONVERSATIONS_REQUEST_MORE,
  FETCH_ALL_CONVERSATIONS_SUCCESS,
} from 'shared/store/actions/conversation/constants'
import { recievedUserFinishedTyping, recievedUserStartedTyping } from 'shared/store/actions/endUser'
import { bulkMessageReadAsync } from 'shared/store/actions/message'
import { closeAllMyConversationsAsync } from 'shared/store/actions/user'
import { ExtendedAttachment } from 'shared/typings/conversations/Attachment'
import Conversation from 'shared/typings/conversations/Conversation'
import { Counts } from 'shared/typings/conversations/Counts'
import { FileUpload } from 'shared/typings/conversations/FileUpload'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'
import { UsersTypingForConversation } from 'shared/typings/endUser/Presence'
import { Tag } from 'shared/typings/tags/Tag'
import spacetime from 'spacetime'

import { AnyRootAction, createReducer } from 'typesafe-actions'

type conversationUnreadStatusById = Record<number, string>
type UsersTypingByConversationId = Record<number, UsersTypingForConversation>
type TagsById = Record<number, Tag>
type FileUploadsByConversationId = Record<number, (FileUpload | ExtendedAttachment)[]>

type FailedFileUploadsByUploadId = Record<number, boolean>

export interface ConversationListItemType {
  id: number
  timestamp: number
}

const mostRecentReadMessageIdByConversation = createReducer<Record<number, number>, AnyRootAction>(
  {}
).handleAction(receiveMostRecentlyReadMessageForConversation, (state, action) => {
  const { payload } = action
  return produce(state, (draft) => {
    draft[payload.conversationId] = payload.messageId
  })
})

const pendingReadReceiptByConversation = createReducer<Record<number, number>, AnyRootAction>(
  {}
).handleAction(updatePendingReadReceipt, (state, action) => {
  const { payload } = action
  return produce(state, (draft) => {
    if (!payload.messageId) {
      delete draft[payload.conversationId]
    } else {
      draft[payload.conversationId] = payload.messageId
    }
  })
})

const typingUserTypesByConvoId = createReducer<UsersTypingByConversationId, AnyRootAction>({})
  .handleAction(recievedUserStartedTyping, (state, action) => {
    return produce(state, (draft) => {
      const { payload } = action
      if (!draft[payload.conversationId]) {
        draft[payload.conversationId] = {}
      }
      draft[payload.conversationId][payload.userId] = payload.userType
    })
  })
  .handleAction(recievedUserFinishedTyping, (state, action) => {
    return produce(state, (draft) => {
      const { payload } = action
      if (!draft[payload.conversationId]) {
        draft[payload.conversationId] = {}
      }

      delete draft[payload.conversationId][payload.userId]
    })
  })

// TODO - fix devtools extension to show Sets
const conversationsBySubscription = createReducer<Record<string, Set<number>>, AnyRootAction>({})
  .handleAction(receiveSubscriptionEvent, (state, action) => {
    const { conversation, eventType, subscriptionId } = action.payload
    const { id: convoId } = conversation

    return produce(state, (draft) => {
      switch (eventType) {
        case 'ADD_CONVERSATION':
          draft[subscriptionId].add(convoId)
          break
        case 'REMOVE_CONVERSATION':
          draft[subscriptionId].delete(convoId)
          break
        default:
      }
    })
  })
  .handleAction(fetchAllConversationsAsync.success, (state, action) => {
    const {
      subscriptionId,
      response: { data: convos },
      refreshState,
    } = action.payload
    const ids = convos.map(({ id }: Conversation) => id)

    return produce(state, (draft) => {
      if (subscriptionId) {
        if (!draft[subscriptionId] || refreshState) {
          draft[subscriptionId] = new Set()
        }
        ids.forEach((id: number) => draft[subscriptionId].add(id))
      }
    })
  })

const allConversations = createReducer<Record<number, Conversation>, AnyRootAction>({})
  .handleAction(fetchAllConversationsAsync.request, (state, action) => {
    if (action.payload.refreshState) {
      return {}
    }
    return state
  })
  .handleAction(fetchAllConversationsAsync.success, (state, action) => {
    return produce(state, (draft) => {
      for (const conversation of action.payload.response.data) {
        const oldLastUpdated = draft[conversation.id]?.updatedAt
        const currentUpdated = conversation.updatedAt
        if (currentUpdated >= oldLastUpdated || oldLastUpdated === undefined) {
          draft[conversation.id] = conversation
        }
      }
    })
  })
  .handleAction(closeAllMyConversationsAsync.request, (state, action) => {
    const myId = action.payload.id

    return produce(state, (draft) => {
      return Object.values(draft).reduce((acc: any, conversation: any) => {
        if (conversation.participantIds.includes(myId)) {
          acc[conversation.id] = { ...conversation, status: ConversationStatus.CLOSED }
        }
        return acc
      }, {})
    })
  })
  .handleAction(
    [receiveConversationUpdateFromWebsocket, fetchConversationAsync.success],
    (state, action) => {
      return produce(state, (draft) => {
        if (!action.payload.lastReadMessageTimestamp) {
          const oldLastReadMessageTimestamp = draft[action.payload.id]?.lastReadMessageTimestamp
          draft[action.payload.id] = action.payload
          draft[action.payload.id].lastReadMessageTimestamp = oldLastReadMessageTimestamp
        } else {
          draft[action.payload.id] = action.payload
        }
      })
    }
  )
  .handleAction([bulkMessageReadAsync.success, receiveManuallyMarkedAsRead], (state, action) => {
    return produce(state, (draft) => {
      const convo = draft[action.payload.conversationId]
      if (!convo) {
        return state
      }
      convo.lastReadMessageTimestamp = spacetime.now().epoch
    })
  })

const countsBySectionUUID = createReducer<Record<string, Counts>>({})
  .handleAction([receiveCounts], (state, action) => {
    return {
      ...state,
      ...action.payload,
    }
  })
  .handleAction(decrementCount, (state: any, action) => {
    const { status, sectionUUID } = action.payload
    if (!state[sectionUUID] || !status) {
      return state
    }
    const lowerStatus = status.toLowerCase()
    return produce(state, (draft: any) => {
      draft[sectionUUID][lowerStatus] = draft[sectionUUID][lowerStatus] - 1
    })
  })
  .handleAction(incrementCount, (state: any, action) => {
    const { status, sectionUUID } = action.payload
    if (!state[sectionUUID] || !status) {
      return state
    }
    const lowerStatus = status.toLowerCase()

    return produce(state, (draft: any) => {
      draft[sectionUUID][lowerStatus] = draft[sectionUUID][lowerStatus] + 1
    })
  })

const badgeCounts = createReducer<Record<number, Record<number, number>>>({})
  .handleAction([receiveBadgeCounts], (state, action) => {
    return action.payload
  })
  .handleAction(decrementBadgeCount, (state, action) => {
    const { inboxId, conversationId } = action.payload
    return produce(state, (draft) => {
      if (inboxId && draft[inboxId]) {
        delete draft[inboxId][conversationId]
      }
    })
  })
  .handleAction(incrementBadgeCount, (state, action) => {
    const { inboxId, conversationId, updatedAt } = action.payload
    return produce(state, (draft) => {
      if (inboxId && !draft[inboxId]) draft[inboxId] = {}
      draft[inboxId][conversationId] = updatedAt
    })
  })

const slashCommand = createReducer<ParsedSlashCommand | null>(null)
  .handleAction([receiveSlashCommand], (state, action) => {
    return action.payload
  })
  .handleAction([clearSlashCommand], () => {
    return null
  })

const conversationUnreadStatusById = createReducer<conversationUnreadStatusById, AnyRootAction>(
  {}
).handleAction(
  [bulkMessageReadAsync.request, bulkMessageReadAsync.success, bulkMessageReadAsync.cancel],
  (state, action) =>
    produce(state, (draft) => {
      draft[action.payload.conversationId] = action.type
    })
)

const allConversationsPagination = createReducer<
  Record<
    string,
    {
      more: boolean
      next?: string
    }
  >,
  AnyRootAction
>({})
  .handleAction(fetchAllConversationsAsync.request, (state, action) => {
    if (action.payload.refreshState) {
      return {}
    }
    return state
  })
  .handleAction(fetchAllConversationsAsync.success, (state, action) => {
    return produce(state, (draft) => {
      draft[action.payload.viewType] = {
        more: action.payload.response.pagination.more,
        next: action.payload.response.pagination.next,
      }
    })
  })

const fetchAllConversationsStatus = createReducer<Record<string, string>, AnyRootAction>(
  {}
).handleAction(
  [
    fetchAllConversationsAsync.success,
    fetchAllConversationsAsync.request,
    fetchAllConversationsAsync.failure,
  ],
  (state, action) => {
    return produce(state, (draft) => {
      if (
        draft[action.payload.viewType] === FETCH_ALL_CONVERSATIONS_SUCCESS &&
        action.type === FETCH_ALL_CONVERSATIONS_REQUEST
      ) {
        draft[action.payload.viewType] = FETCH_ALL_CONVERSATIONS_REQUEST_MORE
      } else {
        draft[action.payload.viewType] = action.type
      }
    })
  }
)

const tagsById = createReducer<TagsById, AnyRootAction>({})
  .handleAction(fetchTagsAsync.success, (_, action) =>
    action.payload.reduce((acc: Record<number, Tag>, tag: Tag) => {
      acc[tag.id] = tag
      return acc
    }, {})
  )
  .handleAction(createTagAsync.success, (state, action) =>
    produce(state, (draft) => {
      draft[action.payload.id] = action.payload
    })
  )

const fileUploadsByConversationId = createReducer<FileUploadsByConversationId, AnyRootAction>({})
  .handleAction(uploadFileAsync.request, (state, action) =>
    produce(state, (draft) => {
      if (!draft[action.payload.conversationId]) {
        draft[action.payload.conversationId] = []
      }
      const file = action.payload.file
      draft[action.payload.conversationId].push({
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        uploadId: action.payload.uploadId,
      })
    })
  )
  .handleAction(uploadFileAsync.failure, (state, action) =>
    produce(state, (draft) => {
      const { file, uploadId, conversationId } = action.payload

      if (!draft[conversationId]) {
        draft[conversationId] = []
      }

      draft[conversationId] = reject<FileUpload>(
        (fileUpload) => fileUpload.uploadId === uploadId,
        draft[conversationId]
      )

      draft[conversationId].push({
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        uploadId: action.payload.uploadId,
      })
    })
  )
  .handleAction(uploadFileAsync.success, (state, action) =>
    produce(state, (draft) => {
      const { attachment, uploadId } = action.payload

      if (!draft[attachment.conversationId]) {
        draft[attachment.conversationId] = []
      }

      draft[attachment.conversationId] = reject<FileUpload | ExtendedAttachment>(
        (fileUpload) => fileUpload.uploadId === uploadId,
        draft[attachment.conversationId]
      )

      draft[attachment.conversationId].push({ ...attachment, uploadId })
    })
  )
  .handleAction(removeFileUpload, (state, action) =>
    produce(state, (draft) => {
      draft[action.payload.conversationId] = reject<FileUpload | ExtendedAttachment>(
        (fileUpload) => fileUpload.uploadId === action.payload.uploadId,
        draft[action.payload.conversationId]
      )
    })
  )
  .handleAction(removePendingAttachments, (state, action) =>
    produce(state, (draft) => {
      draft[action.payload.conversationId] = []
    })
  )

const failedFileUploadByUploadId = createReducer<FailedFileUploadsByUploadId, AnyRootAction>(
  {}
).handleAction(uploadFileAsync.failure, (state, action) =>
  produce(state, (draft) => {
    draft[action.payload.uploadId] = true
  })
)

const syncTime = createReducer<number | null, AnyRootAction>(null)
  .handleAction(receivedConversationSync, (_, action) => action.payload.syncTime)
  .handleAction(fetchConversationLastSyncedAsync.success, (_, action) => action.payload ?? null)

const routedNotRespondedConversations = createReducer<number[], any>([])
  .handleAction(addRoutedNotRespondedConversation, (state, action) => {
    return produce(state, (draft) => {
      if (!draft.includes(action.payload.conversationId)) {
        draft.push(action.payload.conversationId)
      }
    })
  })
  .handleAction(removeRoutedNotRespondedConversation, (state, action) => {
    return reject<number>((item) => item === action.payload.conversationId, state)
  })

const translationSettingsVisibility = createReducer<boolean, AnyRootAction>(false)
  .handleAction(showConversationTranslationSettings, () => true)
  .handleAction(hideConversationTranslationSettings, () => false)

export default combineReducers({
  mostRecentReadMessageIdByConversation,
  conversationUnreadStatusById,
  pendingReadReceiptByConversation,
  allConversations,
  fetchAllConversationsStatus,
  conversationsBySubscription,
  typingUserTypesByConvoId,
  tagsById,
  fileUploadsByConversationId,
  failedFileUploadByUploadId,
  allConversationsPagination,
  syncTime,
  routedNotRespondedConversations,
  countsBySectionUUID,
  badgeCounts,
  slashCommand,
  translationSettingsVisibility,
})
